import { v4 as uuidv4 } from 'uuid';
import Logger from "../utils/Logger";

/**
 * Owns client_id on analytics events. Adopts the id the Amplify Pinpoint provider
 * persisted so client_client_id stays continuous across the cutover.
 *
 * Amplify stores the endpoint id in localStorage under
 * 'aws-amplify-cacheAWSPinpoint_<appId>', wrapped in a JSON envelope with the value
 * under 'data' (verified against Amplify Cache 3.1.49). Amplify mints it with uuid
 * v1, so the shape check is version-agnostic. Amplify expired it after ~3 days; this
 * module persists with no TTL, so ids become more stable (distinct-client_id counts
 * fall for anyone using it as a device proxy).
 *
 * Storage access and logging are guarded: a storage failure degrades to an in-memory
 * id, and Logger.log is null until setLogger() runs, so an unguarded log on a failure
 * path would turn a handled failure into an unhandled exception.
 */
const logWarn = (message) => {
    if (Logger.log) { Logger.log.warn(message); }
};
const logInfo = (message) => {
    if (Logger.log) { Logger.log.info(message); }
};
const OWN_KEY = "dolphin_analytics_client_id";
const AMPLIFY_KEY_PREFIX = "aws-amplify-cacheAWSPinpoint_";
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let cachedClientId = null;

const readQuietly = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch (e) {
        logWarn(`Could not read ${key} from localStorage: ${e}`);
        return null;
    }
};

const writeQuietly = (key, value) => {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        // Kept in memory regardless, so behaviour is stable for the life of the page.
        logWarn(`Could not persist ${key} to localStorage: ${e}`);
    }
};

/**
 * Extracts the endpoint id from Amplify Cache's stored envelope. Tries 'data' (the
 * verified key), then 'value', then a bare string, and shape-validates before use:
 * adopting garbage would corrupt client_client_id for the life of the device.
 */
const readAmplifyEndpointId = (appId) => {
    const raw = readQuietly(AMPLIFY_KEY_PREFIX + appId);
    if (!raw) {
        return null;
    }

    let candidate = raw;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
            candidate = parsed.data || parsed.value || null;
        } else if (typeof parsed === "string") {
            candidate = parsed;
        }
    } catch (e) {
        // Not JSON; treat the raw string as the candidate.
    }

    if (typeof candidate === "string" && UUID_SHAPE.test(candidate)) {
        return candidate;
    }
    logWarn("Stored Amplify endpoint id was not a well formed uuid; not adopting it");
    return null;
};

export default {
    /** Returns the stable client id for this device, resolving it at most once. */
    get(appId) {
        if (cachedClientId) {
            return cachedClientId;
        }

        let clientId = readQuietly(OWN_KEY);

        if (!clientId) {
            clientId = readAmplifyEndpointId(appId);
            if (clientId) {
                logInfo("Adopted the pre-existing analytics client id");
            } else {
                clientId = uuidv4();
                logInfo("No pre-existing analytics client id; generated a new one");
            }
            writeQuietly(OWN_KEY, clientId);
        }

        cachedClientId = clientId;
        return clientId;
    },

    /** Test seam. Clears the cached id. */
    reset() {
        cachedClientId = null;
    }
};
